// Canonical world-space layout of the honeycomb — shared by the renderer and
// the simulation's spatial collision (sizes are real, in pixels).
//
// The world is a torus: the lattice tiles the plane with period vectors U
// (cols axis) and V (rows axis). All distance math must go through
// `nearestCopy` so positions near the seam behave as if the plane repeats.

import type { Vertex, Edge, HexGrid } from './grid.js';
import type { WorldDims } from './coords.js';

/** Edge length in pixels — drives the whole layout scale. */
export const HEX_UNIT = 42;

export interface Px {
  x: number;
  y: number;
}

/**
 * Two-sublattice honeycomb with proper 120° Y-junctions:
 *   A(q, r) = ( (q − r)·L√3/2,  (q + r)·3L/2 )
 *   B(q, r) = A(q, r) + (0, L)
 * Vertex.pos uses doubled coordinates: (2q, 2r) for A, (2q+1, 2r+1) for B.
 */
export function vertexToPx(v: Vertex): Px {
  const type = v.pos.q % 2; // 0 = A, 1 = B
  const q = (v.pos.q - type) / 2;
  const r = (v.pos.r - type) / 2;
  const L = HEX_UNIT;
  const SQRT3_2 = Math.sqrt(3) / 2;
  const ax = (q - r) * L * SQRT3_2;
  const ay = ((q + r) * 3 * L) / 2;
  return { x: ax, y: type === 0 ? ay : ay + L };
}

/** Torus period vectors in pixel space: shifting q by cols / r by rows. */
export function worldPeriods(dims: WorldDims): { u: Px; v: Px } {
  const L = HEX_UNIT;
  const SQRT3_2 = Math.sqrt(3) / 2;
  return {
    u: { x: dims.cols * L * SQRT3_2, y: (dims.cols * 3 * L) / 2 },
    v: { x: -dims.rows * L * SQRT3_2, y: (dims.rows * 3 * L) / 2 },
  };
}

/** The wrapped copy of `p` (p + iU + jV, i,j ∈ −1..1) nearest to `ref`. */
export function nearestCopy(p: Px, ref: Px, dims: WorldDims): Px {
  const { u, v } = worldPeriods(dims);
  let best = p;
  let bestD = Infinity;
  for (let i = -1; i <= 1; i++) {
    for (let j = -1; j <= 1; j++) {
      const x = p.x + i * u.x + j * v.x;
      const y = p.y + i * u.y + j * v.y;
      const dx = x - ref.x;
      const dy = y - ref.y;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = { x, y };
      }
    }
  }
  return best;
}

/**
 * Edge endpoints with the `to` end unwrapped to the copy nearest `from`,
 * so wrap edges behave as short local segments instead of long diagonals.
 */
export function edgeEndpoints(grid: HexGrid, edge: Edge): { from: Px; to: Px } {
  const from = vertexToPx(grid.vertices[edge.from]!);
  const to = nearestCopy(vertexToPx(grid.vertices[edge.to]!), from, grid.dims);
  return { from, to };
}

/** World-space px for an entity at (edgeId, t), seam-safe. */
export function edgeParamToPx(grid: HexGrid, edgeId: number, t: number): Px {
  const edge = grid.edges[edgeId]!;
  const { from, to } = edgeEndpoints(grid, edge);
  return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
}

/** Torus-shortest squared distance between two world-space points. */
export function torusDist2(a: Px, b: Px, dims: WorldDims): number {
  const nb = nearestCopy(b, a, dims);
  const dx = nb.x - a.x;
  const dy = nb.y - a.y;
  return dx * dx + dy * dy;
}
